/**
 * template_generator.js
 * ---------------------
 * Génère fields_schema (JSON Schema draft-07), layout et pdf_overlay
 * depuis les annotations créées dans le PDF Builder.
 *
 * Usage :
 *   import { generateFromAnnotations } from './template_generator';
 *   const { fields_schema, layout, pdf_overlay } = generateFromAnnotations(annotations);
 *
 * Structure d'une annotation :
 * {
 *   id:           string,
 *   page:         number,            // index 0-based
 *   rect: { x, y, width, height },  // coordonnées normalisées [0..1], origin top-left
 *   fieldKey:     string,            // ex: "stats.strength"
 *   fieldType:    string,            // text|number|multiline|checkbox|select|resource_bar|dice_value|repeatable_list
 *   label:        string,
 *   sectionTitle: string,
 *   permissions: {
 *     editable_by: string,           // "player" | "gm"
 *     visibility:  string,           // "public" | "gm_only"
 *   },
 *   validations: {
 *     required:  boolean,
 *     min:       string | number,
 *     max:       string | number,
 *     regex:     string,
 *     options:   string[],           // pour select
 *   },
 *   fontSize:        number,         // défaut 10
 *   resourceMaxKey:  string,         // pour resource_bar
 * }
 */

// ── Constantes ────────────────────────────────────────────────────────────────

const FIELD_TYPE_TO_JSON_TYPE = {
  text: 'string',
  multiline: 'string',
  number: 'integer',
  checkbox: 'boolean',
  select: 'string',
  resource_bar: 'integer',
  dice_value: 'string',
  repeatable_list: 'array',
};

// ── Helpers schéma ────────────────────────────────────────────────────────────

/**
 * Construit la propriété JSON Schema pour une annotation.
 * @param {object} annotation
 * @returns {object} propriété JSON Schema
 */
function buildSchemaProperty(annotation) {
  const { fieldType, label, validations = {} } = annotation;
  const jsonType = FIELD_TYPE_TO_JSON_TYPE[fieldType] || 'string';

  const prop = { type: jsonType };
  if (label) prop.title = label;

  if (fieldType === 'number' || fieldType === 'resource_bar') {
    const min = validations.min !== undefined && validations.min !== '' ? Number(validations.min) : undefined;
    const max = validations.max !== undefined && validations.max !== '' ? Number(validations.max) : undefined;
    if (min !== undefined && !isNaN(min)) prop.minimum = min;
    if (max !== undefined && !isNaN(max)) prop.maximum = max;
  }

  if (fieldType === 'select' && Array.isArray(validations.options) && validations.options.length > 0) {
    prop.enum = validations.options;
  }

  if (fieldType === 'repeatable_list') {
    prop.items = { type: 'string' };
  }

  // Flag temporaire pour required (retiré après insertion dans le schéma)
  if (validations.required) {
    prop._required = true;
  }

  return prop;
}

/**
 * Insère une propriété dans l'arbre imbriqué du schéma via un chemin dot-notation.
 * Ex : "stats.strength" → properties.stats.properties.strength
 * Gère aussi les chemins à un seul segment ("name" → properties.name).
 *
 * @param {object} schemaNode  - nœud courant du schéma (doit avoir une clé "properties")
 * @param {string} keyPath     - chemin dot-notation
 * @param {object} property    - propriété à insérer
 */
function setNestedSchemaProperty(schemaNode, keyPath, property) {
  const parts = keyPath.split('.');
  let current = schemaNode;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!current.properties) current.properties = {};
    if (!current.properties[part]) {
      current.properties[part] = { type: 'object', properties: {} };
    }
    current = current.properties[part];
  }

  const lastKey = parts[parts.length - 1];
  if (!current.properties) current.properties = {};
  current.properties[lastKey] = property;

  // Propagation du required
  if (property._required) {
    if (!current.required) current.required = [];
    if (!current.required.includes(lastKey)) current.required.push(lastKey);
    delete property._required;
  }
}

// ── Helpers layout ────────────────────────────────────────────────────────────

/**
 * Détermine l'accès d'une section depuis les annotations qu'elle contient.
 * Si tous les champs sont gm_only → access.player = "hidden".
 *
 * @param {object[]} sectionAnnotations
 * @returns {object} section access object
 */
function getSectionAccess(sectionAnnotations) {
  const allGmOnly = sectionAnnotations.every(
    (a) => a.permissions?.visibility === 'gm_only'
  );
  return {
    player: allGmOnly ? 'hidden' : 'read_write',
    gm: 'read_write',
    other_players: allGmOnly ? 'hidden' : 'read',
  };
}

// ── Helpers overlay ───────────────────────────────────────────────────────────

/**
 * Génère la structure pdf_overlay depuis les annotations.
 *
 * @param {object[]} annotations
 * @returns {{ pages: object[] }}
 */
function generatePdfOverlay(annotations) {
  // Groupe par numéro de page (0-based)
  const byPage = {};
  for (const ann of annotations) {
    const pageIdx = ann.page ?? 0;
    if (!byPage[pageIdx]) byPage[pageIdx] = [];
    byPage[pageIdx].push(ann);
  }

  const pages = Object.keys(byPage)
    .map(Number)
    .sort((a, b) => a - b)
    .map((pageIdx) => ({
      page: pageIdx,
      fields: byPage[pageIdx].map((ann) => {
        const field = {
          field_key: ann.fieldKey,
          field_type: ann.fieldType,
          x: ann.rect.x,
          y: ann.rect.y,
          width: ann.rect.width,
          height: ann.rect.height,
          font_size: ann.fontSize ?? 10,
        };
        if (ann.fieldType === 'resource_bar' && ann.resourceMaxKey) {
          field.resource_max_key = ann.resourceMaxKey;
        }
        return field;
      }),
    }));

  return { pages };
}

// ── Fonction principale ───────────────────────────────────────────────────────

/**
 * Génère fields_schema, layout et pdf_overlay depuis les annotations du builder.
 *
 * @param {object[]} annotations - liste des annotations
 * @returns {{ fields_schema: object, layout: object, pdf_overlay: object }}
 */
export function generateFromAnnotations(annotations) {
  if (!annotations || annotations.length === 0) {
    return {
      fields_schema: {
        type: 'object',
        required: [],
        additionalProperties: false,
        properties: {},
      },
      layout: { sections: [] },
      pdf_overlay: { pages: [] },
    };
  }

  // ── fields_schema ──────────────────────────────────────────────────────────
  const fields_schema = {
    type: 'object',
    additionalProperties: false,
    properties: {},
  };

  for (const ann of annotations) {
    if (!ann.fieldKey) continue;
    const property = buildSchemaProperty(ann);
    setNestedSchemaProperty(fields_schema, ann.fieldKey, property);
  }

  // ── layout ─────────────────────────────────────────────────────────────────
  // Groupe les annotations par sectionTitle (ordre d'apparition)
  const sectionMap = new Map(); // sectionTitle → annotations[]
  for (const ann of annotations) {
    if (!ann.fieldKey) continue;
    const section = ann.sectionTitle?.trim() || 'Général';
    if (!sectionMap.has(section)) sectionMap.set(section, []);
    sectionMap.get(section).push(ann);
  }

  const sections = [];
  let sectionIdx = 0;
  for (const [sectionTitle, anns] of sectionMap.entries()) {
    const access = getSectionAccess(anns);
    sections.push({
      id: `section_${sectionIdx++}`,
      title: sectionTitle,
      icon: '',
      fields: anns.map((a) => a.fieldKey),
      access,
      collapsed_by_default: false,
    });
  }

  const layout = { sections };

  // ── pdf_overlay ────────────────────────────────────────────────────────────
  const pdf_overlay = generatePdfOverlay(annotations);

  return { fields_schema, layout, pdf_overlay };
}
